import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import talentLogo2 from "../assets/talentlogo2.png";
import usePageMeta from "../hooks/usePageMeta";
import DateTimePickerPanel from "../components/DateTimePicker.jsx";
import { BASE_URL } from "../config";
import { loadManageContext, saveManageContext } from "./manageContext";
import { manageFetch } from "./manageSession";
import { checkInTimeStatusFromManageRow } from "./manageCheckInTimeStatus";
import StaffAvatarCircle from "../components/StaffAvatarCircle";
import {
  formatAppDateTime,
  formatAppIntlLabel,
  formatAppTime,
  fromDatetimeLocalValue,
  getAppDisplayTimezone,
  parseDbDateTime,
  toAppDateKey,
  toDatetimeLocalValue,
} from "../utils/appDateTime";

function normalizeState(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
}

function isNoShowRow(row) {
  return [
    row?.stateRaw,
    row?.statusKey,
    row?.checkInState,
    row?.currentStatus,
  ].some((value) => normalizeState(value).includes("noshow"));
}

function toDateKey(value) {
  return toAppDateKey(value);
}

const HIDE_ROWS_BEFORE_DATE = "2026-07-01";

function dateHeader(value) {
  if (!value) return "Unknown";
  return formatAppIntlLabel(value, {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: getAppDisplayTimezone(),
  });
}

function formatRange(start, end) {
  if (!start || !end) return "-";
  return `${formatAppTime(start)} - ${formatAppTime(end)}`;
}

function formatHoursTruncated(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  const truncated = Math.trunc(n * 100) / 100;
  return truncated.toFixed(2);
}

function hoursAfterBreakMinutes(start, end, breakMinutes) {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return "0h";
  let hours = Math.max(0, (e.getTime() - s.getTime()) / 3600000);
  const b = Number(breakMinutes) || 0;
  if (b > 0) {
    hours = Math.max(0, hours - b / 60);
  }
  return `${formatHoursTruncated(hours)}h`;
}

function roundedHoursAfterBreakMinutes(
  startLocalValue,
  endLocalValue,
  breakMinutes,
  settings,
  row = null,
) {
  const roundedStart = roundDateTimeLocalValue(
    startLocalValue,
    settings?.checkInMode,
    settings,
    { field: "checkin", scheduledStart: row?.startIso },
  );
  const roundedEnd = roundDateTimeLocalValue(
    endLocalValue,
    settings?.checkOutMode,
    settings,
    { field: "checkout", scheduledEnd: row?.endIso },
  );
  return hoursAfterBreakMinutes(
    fromDateTimeLocalValue(roundedStart),
    fromDateTimeLocalValue(roundedEnd),
    breakMinutes,
  );
}

function toDateTimeLocalValue(value) {
  if (value === null || value === undefined || value === "" || value === 0 || value === "0") {
    return "";
  }
  const d = parseDbDateTime(value);
  if (!d) return "";
  if (d.getUTCFullYear() === 1970 && d.getUTCMonth() === 0 && d.getUTCDate() === 1) return "";
  return toDatetimeLocalValue(d);
}

function formatPickerDisplayValue(value) {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (raw.startsWith("1970-01-01")) return "";
  const localMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (localMatch) {
    const year = Number(localMatch[1]);
    const month = Number(localMatch[2]);
    const day = Number(localMatch[3]);
    const hour = Number(localMatch[4]);
    const minute = Number(localMatch[5]);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${month}/${day}/${year} ${hour12}:${String(minute).padStart(2, "0")} ${ampm}`;
  }
  const parsed = parseDbDateTime(raw);
  if (!parsed) return raw;
  return formatAppDateTime(parsed);
}

function fromDateTimeLocalValue(value) {
  if (!value) return null;
  if (String(value).startsWith("1970-01-01")) return null;
  const d = fromDatetimeLocalValue(value);
  if (!d) return null;
  return d.toISOString();
}

function normalizeRoundingMode(value) {
  const mode = String(value || "nearest").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (["up", "increase", "ceil", "round_up", "roundup"].includes(mode)) return "up";
  if (["down", "decrease", "floor", "round_down", "rounddown"].includes(mode)) return "down";
  return "nearest";
}

function isRoundingEnabledValue(value) {
  if (value === true) return true;
  const raw = String(value ?? "").trim().toLowerCase();
  if (raw === "true") return true;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0;
}

function normalizeSignoffRoundingSettings(data) {
  const graceTime = Number(data?.grace_time ?? data?.graceTime);
  const roundingRaw = data?.grace_time_rounding ?? data?.graceTimeRounding;
  const intervalMinutes = Number(roundingRaw);
  const enabled =
    isRoundingEnabledValue(roundingRaw) &&
    Number.isFinite(intervalMinutes) &&
    intervalMinutes > 0;

  return {
    enabled,
    intervalMinutes: enabled ? intervalMinutes : 0,
    graceWindowMinutes: Number.isFinite(graceTime) && graceTime > 0 ? graceTime : 0,
    checkInMode: normalizeRoundingMode(data?.check_in_rounding ?? data?.checkInRounding),
    checkOutMode: normalizeRoundingMode(data?.check_out_rounding ?? data?.checkOutRounding),
  };
}

function applyScheduledGraceSnap(date, settings, options = {}) {
  if (!date || !settings?.enabled) return { date, snapped: false };
  const graceWindowMinutes = Number(settings.graceWindowMinutes);
  if (!Number.isFinite(graceWindowMinutes) || graceWindowMinutes <= 0) {
    return { date, snapped: false };
  }

  const field = options.field;
  const scheduledValue =
    field === "checkin" ? options.scheduledStart : options.scheduledEnd;
  const scheduledDate = scheduledValue ? parseDbDateTime(scheduledValue) : null;
  if (!scheduledDate) return { date, snapped: false };

  const diffMinutes = (date.getTime() - scheduledDate.getTime()) / 60000;
  if (field === "checkin" && diffMinutes >= 0 && diffMinutes <= graceWindowMinutes) {
    return { date: scheduledDate, snapped: true };
  }
  if (field === "checkout" && diffMinutes <= 0 && diffMinutes >= -graceWindowMinutes) {
    return { date: scheduledDate, snapped: true };
  }
  return { date, snapped: false };
}

function roundDateTimeLocalValue(value, mode, settings, options = {}) {
  if (!value || !settings?.enabled) return value || "";
  const intervalMinutes = Number(settings.intervalMinutes);
  if (!Number.isFinite(intervalMinutes) || intervalMinutes <= 0) return value;

  const snapResult = applyScheduledGraceSnap(
    fromDatetimeLocalValue(value),
    settings,
    options,
  );
  const date = snapResult.date;
  if (!date) return value;
  if (snapResult.snapped) return toDatetimeLocalValue(date);
  if (intervalMinutes === 1) return toDatetimeLocalValue(date);

  const stepMs = intervalMinutes * 60000;
  const currentMs = date.getTime();
  const normalizedMode = normalizeRoundingMode(mode);
  let roundedMs = currentMs;

  if (normalizedMode === "up") {
    roundedMs = Math.ceil(currentMs / stepMs) * stepMs;
  } else if (normalizedMode === "down") {
    roundedMs = Math.floor(currentMs / stepMs) * stepMs;
  } else {
    roundedMs = Math.round(currentMs / stepMs) * stepMs;
  }

  return toDatetimeLocalValue(new Date(roundedMs));
}

function hoursValueMinutes(start, end, breakMinutes) {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  let hours = Math.max(0, (e.getTime() - s.getTime()) / 3600000);
  const b = Number(breakMinutes) || 0;
  if (b > 0) {
    hours = Math.max(0, hours - b / 60);
  }
  return hours;
}

function sameInstantMinute(a, b) {
  const da = parseDbDateTime(a);
  const db = parseDbDateTime(b);
  if (!da || !db) return false;
  return Math.floor(da.getTime() / 60000) === Math.floor(db.getTime() / 60000);
}

const CLIENT_DEFAULT_BREAK_MINUTES = 30;

/** clients.unpaid_break is stored as 0, 1 (legacy flag), or 30 (minutes). */
function normalizeClientUnpaidBreakMinutes(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n === 1) return CLIENT_DEFAULT_BREAK_MINUTES;
  return n;
}

function clientHasDefaultBreak(unpaidBreakMinutes) {
  return normalizeClientUnpaidBreakMinutes(unpaidBreakMinutes) >= CLIENT_DEFAULT_BREAK_MINUTES;
}

function isTruthyModifyUnpaidBreak(raw) {
  if (raw === true || raw === 1) return true;
  const normalized = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (!normalized || normalized === "0" || normalized === "false" || normalized === "no") {
    return false;
  }
  return normalized === "1" || normalized === "true" || normalized === "yes";
}

/** Scheduled unpaid break from API row (minutes). */
function scheduledBreakMinutesFromRow(row) {
  const explicitMinutes = Number(row?.scheduledUnpaidBreakMinutes);
  if (Number.isFinite(explicitMinutes) && explicitMinutes > 0) {
    return explicitMinutes;
  }
  const scheduledFlag = Number(row?.scheduledUnpaidBreak);
  if (Number.isFinite(scheduledFlag) && scheduledFlag > 0) {
    if (scheduledFlag === 1) return CLIENT_DEFAULT_BREAK_MINUTES;
    return scheduledFlag >= CLIENT_DEFAULT_BREAK_MINUTES
      ? scheduledFlag
      : Math.round(scheduledFlag * 60);
  }
  return 0;
}

function defaultBreakMinutesForRow(row, clientDefaultBreakMinutes) {
  const scheduled = scheduledBreakMinutesFromRow(row);
  if (scheduled > 0) return scheduled;
  return clientHasDefaultBreak(clientDefaultBreakMinutes)
    ? CLIENT_DEFAULT_BREAK_MINUTES
    : 0;
}

function defaultReviewBreakMinutesForRow(row, clientDefaultBreakMinutes) {
  if (clientDefaultBreakMinutes > 0) return CLIENT_DEFAULT_BREAK_MINUTES;
  return defaultBreakMinutesForRow(row, clientDefaultBreakMinutes);
}

/** When deducting for all shifts, honor the client default break (30 min). */
function breakMinutesWhenDeductAll(row, clientDefaultBreakMinutes) {
  if (clientHasDefaultBreak(clientDefaultBreakMinutes)) {
    return CLIENT_DEFAULT_BREAK_MINUTES;
  }
  return scheduledBreakMinutesFromRow(row) > 0 ? CLIENT_DEFAULT_BREAK_MINUTES : 0;
}

function resolveRowBreakMinutes(
  row,
  deductBreakAllShifts,
  rowActual,
  clientDefaultBreakMinutes,
  allowModifyBreak,
) {
  if (!allowModifyBreak && deductBreakAllShifts && clientDefaultBreakMinutes > 0) {
    return CLIENT_DEFAULT_BREAK_MINUTES;
  }
  if (!allowModifyBreak) {
    return breakMinutesWhenDeductAll(row, clientDefaultBreakMinutes);
  }
  const n = Number(
    rowActual?.actualUnpaidBreakMinutes ??
      row?.actualUnpaidBreakMinutes ??
      defaultReviewBreakMinutesForRow(row, clientDefaultBreakMinutes),
  );
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function manageRowStaffEmployee(row) {
  const accountId = Number(row?.assignedAccountId);
  const name = row?.staffName || "Staff";
  if (!Number.isFinite(accountId) || accountId <= 0) {
    return { name };
  }
  return {
    account_id: accountId,
    accountId,
    name,
    image_time: row?.imageTime ?? row?.image_time ?? null,
    image_type: row?.imageType ?? row?.image_type ?? null,
  };
}

function statusBadgeClasses(stateRaw) {
  const state = normalizeState(stateRaw);
  if (state.includes("noshow")) {
    return "bg-[#c9656a] text-white";
  }
  if (state.includes("complete")) {
    return "bg-[#9ea0b8] text-white";
  }
  if (state.includes("review")) {
    return "bg-[#6ea9c9] text-white";
  }
  return "bg-[#17a8b8] text-white";
}

function normalizeRoleValue(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
}

function isAccountingWeekClosed(row) {
  return String(row?.weekState || "open").toLowerCase() === "closed";
}

export default function ManageSignoff({
  portalMode = false,
  portalClient = null,
  onPortalClose,
  onPortalComplete,
} = {}) {
  usePageMeta(portalMode ? "Sign-off on timesheets" : "Manage Signoff");
  const navigate = useNavigate();
  const location = useLocation();
  const manageContext = useMemo(() => {
    if (portalMode && portalClient) {
      return {
        clientId: portalClient.clientId,
        clientName: portalClient.name,
        capabilities: portalClient.capabilities || null,
        selectedLocationIds: Array.isArray(portalClient.clientLocationIds)
          ? portalClient.clientLocationIds
          : [],
        accountId: portalClient.accountId || null,
        shiftId: null,
        timesheetId: null,
        locationName: "",
        startDate: null,
        endDate: null,
        shiftDate: null,
        clientDeviceId: null,
        email: "",
      };
    }
    return loadManageContext(location);
  }, [portalMode, portalClient, location]);
  const {
    clientName,
    clientId,
    locationName,
    startDate,
    endDate,
    shiftDate,
    shiftId,
    timesheetId,
    capabilities,
    clientDeviceId,
    selectedLocationIds,
  } = manageContext;

  const canManageTimesheetSignoff = capabilities?.canManageTimesheetSignoff === true;

  const handleExit = () => {
    if (portalMode) {
      onPortalClose?.();
      return;
    }
    navigate("/manage/dashboard", { state: { manageContext } });
  };

  const handleSignoffSuccess = () => {
    onPortalComplete?.();
    if (portalMode) {
      onPortalClose?.();
    }
  };

  const [showSetupModal, setShowSetupModal] = useState(true);
  const [deductBreakAllShifts, setDeductBreakAllShifts] = useState(true);
  const [signoffMode, setSignoffMode] = useState("all"); // all | day
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shifts, setShifts] = useState([]);
  const [selectedDateKey, setSelectedDateKey] = useState("");
  const [agreeMap, setAgreeMap] = useState({});
  const [disagreeMap, setDisagreeMap] = useState({});
  const [actualMap, setActualMap] = useState({});
  const [attendancePicker, setAttendancePicker] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAttentionModal, setShowAttentionModal] = useState(false);
  const [postSubmitNote, setPostSubmitNote] = useState("");
  const [reloadNonce, setReloadNonce] = useState(0);
  const [clientTimesheetReviewRequired, setClientTimesheetReviewRequired] = useState(false);
  const [clientUnpaidBreakMinutes, setClientUnpaidBreakMinutes] = useState(null);
  const [clientModifyUnpaidBreak, setClientModifyUnpaidBreak] = useState(false);
  const [shiftSettingsLoaded, setShiftSettingsLoaded] = useState(false);
  const [clientRoundingSettings, setClientRoundingSettings] = useState({
    enabled: false,
    intervalMinutes: 0,
    graceWindowMinutes: 0,
    checkInMode: "nearest",
    checkOutMode: "nearest",
  });
  /** Manager modal: sign off shifts vs review queue (only when client requires review + manager capability). */
  const [signoffIncludeMode, setSignoffIncludeMode] = useState("signoff_on_shifts");
  /** After setup: pending = normal sheet; review = manager review queue (server sheet=review). */
  const [signoffSheetKind, setSignoffSheetKind] = useState("pending");
  const selectedSignoffToDate = useMemo(
    () => shiftDate || endDate || selectedDateKey || null,
    [shiftDate, endDate, selectedDateKey],
  );
  const selectedSignoffFromDate = useMemo(
    () => (signoffMode === "all" ? HIDE_ROWS_BEFORE_DATE : selectedSignoffToDate),
    [signoffMode, selectedSignoffToDate],
  );
  const selectedLocationIdsKey = Array.isArray(selectedLocationIds)
    ? selectedLocationIds.join(",")
    : "";
  const clientDefaultBreakMinutes = normalizeClientUnpaidBreakMinutes(clientUnpaidBreakMinutes);
  const openAttendancePicker = (row, field) => {
    const rowActual = actualMap[String(row.id)] || {};
    const currentValue =
      field === "checkin" ? rowActual.actualStartDate : rowActual.actualEndDate;
    const fallbackInstant =
      field === "checkin" ? parseDbDateTime(row.startIso) : parseDbDateTime(row.endIso);
    setAttendancePicker({
      rowId: String(row.id),
      field,
      value:
        (currentValue ? fromDatetimeLocalValue(currentValue) : null) ||
        fallbackInstant ||
        null,
      minDateTime:
        field === "checkout"
          ? (rowActual.actualStartDate
              ? fromDatetimeLocalValue(rowActual.actualStartDate)
              : parseDbDateTime(row.startIso)) || null
          : null,
    });
  };

  const applyAttendancePickerValue = (date) => {
    if (!attendancePicker) return;
    const rowId = attendancePicker.rowId;
    const fieldKey =
      attendancePicker.field === "checkin" ? "actualStartDate" : "actualEndDate";
    const row = shifts.find((item) => String(item.id) === rowId) || null;
    const roundingMode =
      attendancePicker.field === "checkin"
        ? clientRoundingSettings.checkInMode
        : clientRoundingSettings.checkOutMode;
    const nextValue = date ? toDatetimeLocalValue(date) : "";

    setActualMap((prev) => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
        [fieldKey]: roundDateTimeLocalValue(
          nextValue,
          roundingMode,
          clientRoundingSettings,
          {
            field: attendancePicker.field,
            scheduledStart: row?.startIso,
            scheduledEnd: row?.endIso,
          },
        ),
      },
    }));
  };

  const applyClientShiftSettings = (data) => {
    if (!data) return;
    const unpaidBreakRaw = data.unpaid_break ?? data.unpaidBreak;
    const normalizedBreak = normalizeClientUnpaidBreakMinutes(unpaidBreakRaw);
    const modifyBreak = isTruthyModifyUnpaidBreak(
      data.modify_unpaid_break ?? data.modifyUnpaidBreak,
    );
    setClientUnpaidBreakMinutes(normalizedBreak > 0 ? normalizedBreak : null);
    setClientModifyUnpaidBreak(modifyBreak);
    setShiftSettingsLoaded(true);
  };

  useEffect(() => {
    saveManageContext(manageContext);
  }, [manageContext]);

  useEffect(() => {
    let cancelled = false;
    const clientIdForSettings = Number(clientId);

    if (!Number.isFinite(clientIdForSettings) || clientIdForSettings <= 0) {
      setClientUnpaidBreakMinutes(null);
      setClientModifyUnpaidBreak(false);
      setShiftSettingsLoaded(false);
      setClientRoundingSettings({
        enabled: false,
        intervalMinutes: 0,
        graceWindowMinutes: 0,
        checkInMode: "nearest",
        checkOutMode: "nearest",
      });
      return undefined;
    }

    const loadShiftSettings = async () => {
      if (!cancelled) {
        setShiftSettingsLoaded(false);
        setClientModifyUnpaidBreak(false);
      }
      try {
        const response = await manageFetch(
          `${BASE_URL}/api/clients/${encodeURIComponent(clientIdForSettings)}/shift-settings`,
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.message || `Failed to load shift settings (${response.status})`);
        }
        if (!cancelled) {
          applyClientShiftSettings(payload?.data);
          setClientRoundingSettings(normalizeSignoffRoundingSettings(payload?.data || {}));
        }
      } catch {
        if (!cancelled) {
          setClientUnpaidBreakMinutes(null);
          setClientModifyUnpaidBreak(false);
          setShiftSettingsLoaded(true);
          setClientRoundingSettings({
            enabled: false,
            intervalMinutes: 0,
            graceWindowMinutes: 0,
            checkInMode: "nearest",
            checkOutMode: "nearest",
          });
        }
      }
    };

    loadShiftSettings();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    let cancelled = false;
    const hasTarget =
      (Number.isFinite(Number(shiftId)) && Number(shiftId) > 0) ||
      (Number.isFinite(Number(timesheetId)) && Number(timesheetId) > 0) ||
      (Number.isFinite(Number(clientId)) && Number(clientId) > 0);
    if (!hasTarget) return undefined;

    const loadMeta = async () => {
      try {
        const params = new URLSearchParams();
        if (shiftId) params.set("shiftId", String(shiftId));
        if (timesheetId) params.set("timesheetId", String(timesheetId));
        if (clientId) params.set("clientId", String(clientId));
        if (locationName) params.set("location", String(locationName));
        if (shiftDate) params.set("shiftDate", String(shiftDate));
        if (startDate) params.set("from", String(startDate));
        if (endDate) params.set("to", String(endDate));
        if (clientDeviceId) params.set("clientDeviceId", String(clientDeviceId));
        if (Array.isArray(selectedLocationIds) && selectedLocationIds.length) {
          params.set("selectedLocationIds", selectedLocationIds.join(","));
        }
        params.set("metaOnly", "1");
        const response = await manageFetch(
          `${BASE_URL}/api/jobs/manage/signoff?${params.toString()}`,
        );
        const payload = await response.json().catch(() => ({}));
        if (!cancelled && response.ok && payload?.success) {
          setClientTimesheetReviewRequired(!!payload?.data?.timesheetReviewRequired);
          applyClientShiftSettings(payload?.data);
        }
      } catch {
        if (!cancelled) setClientTimesheetReviewRequired(false);
      }
    };
    loadMeta();
    return () => {
      cancelled = true;
    };
  }, [
    shiftId,
    timesheetId,
    clientId,
    locationName,
    shiftDate,
    startDate,
    endDate,
    clientDeviceId,
    selectedLocationIds,
  ]);

  const useMultidayScope = signoffMode === "all" || signoffMode === "day";

  useEffect(() => {
    if (showSetupModal) return;

    let cancelled = false;

    const loadShifts = async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams();
        if (shiftId) params.set("shiftId", String(shiftId));
        if (timesheetId) params.set("timesheetId", String(timesheetId));
        if (clientId) params.set("clientId", String(clientId));
        if (locationName) params.set("location", String(locationName));
        if (signoffMode === "all") {
          if (selectedSignoffFromDate) params.set("from", String(selectedSignoffFromDate));
          if (selectedSignoffToDate) params.set("to", String(selectedSignoffToDate));
        } else {
          if (selectedSignoffToDate) params.set("shiftDate", String(selectedSignoffToDate));
          if (selectedSignoffToDate) params.set("from", String(selectedSignoffToDate));
          if (selectedSignoffToDate) params.set("to", String(selectedSignoffToDate));
        }
        if (useMultidayScope) params.set("scope", "multiday");
        if (clientDeviceId) params.set("clientDeviceId", String(clientDeviceId));
        if (Array.isArray(selectedLocationIds) && selectedLocationIds.length) {
          params.set("selectedLocationIds", selectedLocationIds.join(","));
        }
        if (signoffSheetKind === "review") {
          params.set("sheet", "review");
        }
        const response = await manageFetch(
          `${BASE_URL}/api/jobs/manage/signoff?${params.toString()}`,
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.message || `Failed to load shifts (${response.status})`);
        }
        const rows = Array.isArray(payload?.data?.shifts)
          ? payload.data.shifts
          : Array.isArray(payload?.data?.rows)
            ? payload.data.rows
            : [];
        let normalized = rows
          .map((row) => {
            const id = row?.id ?? row?.shift_id;
            const startIso = row?.startDate;
            const endIso = row?.endDate;
            const stateRaw = row?.statusKey || row?.actualState || row?.signoffState;
            return {
              id,
              timesheetId:
                row?.timesheetId ||
                row?.shiftAccountId ||
                timesheetId ||
                null,
              startIso,
              endIso,
              stateRaw,
              statusKey: row?.statusKey || row?.status_key || "",
              checkInState: row?.checkInState || row?.check_in_state || "",
              currentStatus: row?.currentStatus || row?.current_status || "",
              stateKey: normalizeState(stateRaw),
              actualState: String(row?.actualState ?? row?.actual_state ?? "").trim(),
              actualStartDate: row?.actualStartDate ?? row?.actual_start_date ?? null,
              actualEndDate: row?.actualEndDate ?? row?.actual_end_date ?? null,
              signoffState: row?.signoffState ?? row?.signoff_state ?? null,
              checkinDate:
                row?.checkinDate ??
                row?.checkin_date ??
                row?.attendanceCheckinDate ??
                row?.attendance_checkin_date ??
                row?.shiftCheckinDate ??
                row?.shift_checkin_date ??
                null,
              checkoutDate:
                row?.checkoutDate ??
                row?.checkout_date ??
                row?.attendanceCheckoutDate ??
                row?.attendance_checkout_date ??
                row?.shiftCheckoutDate ??
                row?.shift_checkout_date ??
                null,
              locationName: row?.locationName || "Location",
              position: row?.position || "Shift",
              staffName: row?.staffName || "Unassigned",
              clientContactName: String(
                row?.clientContactName ?? row?.client_contact_name ?? "",
              ).trim(),
              assignedAccountId: Number(row?.assignedAccountId) > 0 ? Number(row.assignedAccountId) : null,
              actualUnpaidBreakMinutes: row?.actualUnpaidBreakMinutes ?? null,
              scheduledUnpaidBreak: row?.scheduledUnpaidBreak,
              scheduledUnpaidBreakMinutes: row?.scheduledUnpaidBreakMinutes,
              weekState: String(row?.weekState || "open").toLowerCase(),
            };
          })
          .filter((row) => {
            if (!row.id) return false;
            const rowDateKey = toDateKey(row.startIso);
            if (rowDateKey && rowDateKey < HIDE_ROWS_BEFORE_DATE) {
              return false;
            }
            if (!row.assignedAccountId || String(row.staffName || "").trim().toLowerCase() === "unassigned") {
              return false;
            }
            const { badgeClass } = checkInTimeStatusFromManageRow({
              actualState: row.actualState,
              checkoutDate: row.checkoutDate,
              checkinDate: row.checkinDate,
              startDate: row.startIso,
              signoffState: row.signoffState,
            });
            if (signoffSheetKind === "review") {
              return badgeClass === "pending-review";
            }
            return true;
          });

        if (!cancelled && typeof payload?.data?.timesheetReviewRequired === "boolean") {
          setClientTimesheetReviewRequired(!!payload.data.timesheetReviewRequired);
        }
        if (!cancelled && payload?.data) {
          applyClientShiftSettings(payload.data);
        }

        if (signoffMode === "day" && normalized.length) {
          const dateKeys = normalized
            .map((r) => toDateKey(r.startIso))
            .filter(Boolean)
            .sort();
          const earliest = dateKeys[0];
          if (earliest) {
            normalized = normalized.filter((r) => toDateKey(r.startIso) === earliest);
          }
        }

        if (!cancelled) {
          setShifts(normalized);
          const firstDate = normalized[0]?.startIso ? toDateKey(normalized[0].startIso) : "";
          setSelectedDateKey(firstDate || "");
          const nextAgree = {};
          const nextDisagree = {};
          normalized.forEach((row) => {
            const hasCheckIn = !!toDateTimeLocalValue(row.actualStartDate || row.checkinDate);
            const hasCheckOut = !!toDateTimeLocalValue(row.actualEndDate || row.checkoutDate);
            const hasBothOrNeither = hasCheckIn === hasCheckOut;
            nextAgree[String(row.id)] = hasBothOrNeither;
            nextDisagree[String(row.id)] = "";
          });
          setAgreeMap(nextAgree);
          setDisagreeMap(nextDisagree);
          setActualMap((prev) => {
            const next = { ...prev };
            normalized.forEach((row) => {
              const key = String(row.id);
              const actualStartDate = roundDateTimeLocalValue(
                toDateTimeLocalValue(row.actualStartDate || row.checkinDate),
                clientRoundingSettings.checkInMode,
                clientRoundingSettings,
                { field: "checkin", scheduledStart: row.startIso },
              );
              const actualEndDate = roundDateTimeLocalValue(
                toDateTimeLocalValue(row.actualEndDate || row.checkoutDate),
                clientRoundingSettings.checkOutMode,
                clientRoundingSettings,
                { field: "checkout", scheduledEnd: row.endIso },
              );
              next[key] = {
                actualStartDate,
                actualEndDate,
                actualUnpaidBreakMinutes:
                  prev[key]?.actualUnpaidBreakMinutes ??
                  (Number.isFinite(Number(row.actualUnpaidBreakMinutes)) &&
                  Number(row.actualUnpaidBreakMinutes) > 0
                    ? Number(row.actualUnpaidBreakMinutes)
                    : defaultReviewBreakMinutesForRow(row, clientDefaultBreakMinutes)),
              };
            });
            return next;
          });
        }
      } catch (err) {
        if (!cancelled) {
          if (err?.name === "AbortError") {
            setError("Signoff API timeout. Please try again.");
          } else {
            setError(err?.message || "Failed to load signoff sheet");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadShifts();
    return () => {
      cancelled = true;
    };
  }, [
    showSetupModal,
    useMultidayScope,
    shiftId,
    timesheetId,
    clientId,
    locationName,
    selectedSignoffFromDate,
    selectedSignoffToDate,
    signoffMode,
    clientDeviceId,
    selectedLocationIds,
    selectedLocationIdsKey,
    reloadNonce,
    signoffSheetKind,
    clientRoundingSettings,
    clientDefaultBreakMinutes,
  ]);

  const groups = useMemo(() => {
    const grouped = {};
    shifts.forEach((row) => {
      const key = toDateKey(row.startIso) || "unknown";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    });
    return grouped;
  }, [shifts]);

  const orderedDateKeys = useMemo(
    () => Object.keys(groups).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)),
    [groups],
  );

  const activeShifts = useMemo(() => shifts, [shifts]);

  const activeGroups = useMemo(() => {
    const grouped = {};
    activeShifts.forEach((row) => {
      const key = toDateKey(row.startIso) || "unknown";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    });
    return grouped;
  }, [activeShifts]);

  const activeOrderedDateKeys = useMemo(
    () => Object.keys(activeGroups).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)),
    [activeGroups],
  );

  const visibleDateKeys =
    signoffMode === "day"
      ? (selectedDateKey ? [selectedDateKey] : activeOrderedDateKeys.slice(0, 1))
      : activeOrderedDateKeys;

  useEffect(() => {
    if (signoffMode !== "day") return;

    const nextSelected =
      (selectedDateKey && activeGroups[selectedDateKey] ? selectedDateKey : "") ||
      activeOrderedDateKeys[0] ||
      "";

    if (nextSelected !== selectedDateKey) {
      setSelectedDateKey(nextSelected);
    }
  }, [
    signoffMode,
    selectedDateKey,
    activeGroups,
    activeOrderedDateKeys,
  ]);

  const visibleRows = useMemo(() => {
    if (signoffMode !== "day") {
      return activeShifts;
    }

    const onlyDateKey = visibleDateKeys[0] || "";
    if (!onlyDateKey) return [];
    return activeGroups[onlyDateKey] || [];
  }, [signoffMode, activeShifts, visibleDateKeys, activeGroups]);

  const closedWeekRowCounts = useMemo(() => {
    const closed = visibleRows.filter(isAccountingWeekClosed).length;
    return { open: visibleRows.length - closed, closed };
  }, [visibleRows]);

  const buildSubmitPayloadForRows = (fromRows) => {
    const requestBody = {
      shiftId: shiftId || null,
      timesheetId: timesheetId || null,
      clientId: clientId || null,
      location: locationName || null,
      shiftDate: shiftDate || null,
      from: startDate || null,
      to: endDate || null,
      clientDeviceId: clientDeviceId || null,
      selectedLocationIds: Array.isArray(selectedLocationIds) ? selectedLocationIds : [],
      signoffIntent: "signoff_shifts",
      rows: fromRows.map((row) => {
      const rowActual = actualMap[String(row.id)] || {};
      const submittedActualStart =
        fromDateTimeLocalValue(rowActual.actualStartDate) || row.checkinDate || null;
      const submittedActualEnd =
        fromDateTimeLocalValue(rowActual.actualEndDate) || row.checkoutDate || null;
      const hasEnteredActuals = !!(submittedActualStart && submittedActualEnd);
      const isNoShow =
        !hasEnteredActuals &&
        (isNoShowRow(row) || (!submittedActualStart && !submittedActualEnd));
      const accountId = Number(row.assignedAccountId) > 0 ? Number(row.assignedAccountId) : null;

      let actualState = "as_scheduled";
      if (isNoShow) actualState = "no_show";

      const brDifferent = resolveRowBreakMinutes(
        row,
        deductBreakAllShifts,
        rowActual,
        clientDefaultBreakMinutes,
        clientModifyUnpaidBreak,
      );

      const rowPayload = {
        shiftId: row.id,
        ...(accountId ? { accountId } : {}),
      };
      const disagreeNote = String(disagreeMap[String(row.id)] || "").trim();
      const effectiveActualStart =
        submittedActualStart ||
        row.startIso ||
        null;
      const effectiveActualEnd =
        submittedActualEnd ||
        row.endIso ||
        null;
      const scheduledBreakMinutes = scheduledBreakMinutesFromRow(row);
      const effectiveBreakMinutes = resolveRowBreakMinutes(
        row,
        deductBreakAllShifts,
        rowActual,
        clientDefaultBreakMinutes,
        clientModifyUnpaidBreak,
      );
      const matchesScheduledActuals =
        sameInstantMinute(effectiveActualStart, row.startIso) &&
        sameInstantMinute(effectiveActualEnd, row.endIso) &&
        Number(effectiveBreakMinutes || 0) === Number(scheduledBreakMinutes || 0);

      if (actualState === "as_scheduled") {
        if (!matchesScheduledActuals) {
          actualState = "different";
        }
      }

      if (actualState === "as_scheduled") {
        return {
          ...rowPayload,
          actualState,
          actualStartDate: effectiveActualStart,
          actualEndDate: effectiveActualEnd,
          actualUnpaidBreakMinutes: effectiveBreakMinutes,
          note: disagreeNote || null,
        };
      }

      if (actualState === "no_show") {
        return {
          ...rowPayload,
          actualState,
          actualStartDate: null,
          actualEndDate: null,
          actualUnpaidBreakMinutes: 0,
          note: disagreeNote || null,
        };
      }

      return {
        ...rowPayload,
        actualState,
        actualStartDate: fromDateTimeLocalValue(rowActual.actualStartDate) || null,
        actualEndDate: fromDateTimeLocalValue(rowActual.actualEndDate) || null,
        actualUnpaidBreakMinutes: brDifferent,
        note: disagreeNote || null,
      };
    }),
    scope: useMultidayScope ? "multiday" : undefined,
  };
    if (clientTimesheetReviewRequired && canManageTimesheetSignoff) {
      requestBody.signoffSubMode = signoffIncludeMode;
    }
    if (signoffSheetKind === "review" && clientTimesheetReviewRequired && canManageTimesheetSignoff) {
      requestBody.signoffSubMode = "signoff_on_shifts";
    }
    return requestBody;
  };

  const executeSignoffPost = async (fromRows) => {
    if (!fromRows.length) {
      setError("No shifts to submit.");
      return;
    }
    setSubmitting(true);
    setError("");
    setPostSubmitNote("");
    try {
      const body = buildSubmitPayloadForRows(fromRows);
      const response = await manageFetch(`${BASE_URL}/api/jobs/manage/signoff`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.success) {
        if (payload?.code === "ALL_CLOSED_ACCOUNTING_WEEKS") {
          setError(
            payload.message ||
              "All selected shifts fall in a closed accounting week and cannot be signed off.",
          );
          return;
        }
        throw new Error(payload?.message || "Failed to submit actuals");
      }
      const skipped = Array.isArray(payload?.data?.skipped) ? payload.data.skipped : [];
      const submittedCount = Array.isArray(payload?.data?.submitted)
        ? payload.data.submitted.length
        : 0;
      setShowConfirmModal(false);
      setShowAttentionModal(false);
      const parts =
        signoffSheetKind === "review"
          ? [`Successfully completed manager signoff for ${submittedCount} shift(s).`]
          : [`Successfully submitted actuals for ${submittedCount} shift(s).`];
      if (skipped.length > 0) {
        parts.push(`Skipped ${skipped.length} (closed accounting week).`);
      }
      setPostSubmitNote(parts.join(" "));
      setReloadNonce((n) => n + 1);
      if (portalMode) {
        handleSignoffSuccess();
      }
    } catch (err) {
      setError(err?.message || "Failed to submit actuals");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitActuals = () => {
    if (!visibleRows.length) {
      setError("No shifts to submit.");
      return;
    }

    for (const row of visibleRows) {
      if (isAccountingWeekClosed(row)) continue;
      const id = String(row.id);
      const rowActual = actualMap[id] || {};
      const hasEnteredActuals =
        !!fromDateTimeLocalValue(rowActual.actualStartDate) &&
        !!fromDateTimeLocalValue(rowActual.actualEndDate);
      const isNoShow = !hasEnteredActuals && normalizeState(row.stateRaw).includes("noshow");
      if (!agreeMap[id] && !isNoShow) {
        if (!fromDateTimeLocalValue(rowActual.actualStartDate) || !fromDateTimeLocalValue(rowActual.actualEndDate)) {
          setError("Select check-in and check-out for each shift where Agree is unchecked.");
          return;
        }
        if (!String(disagreeMap[id] || "").trim()) {
          setError("Enter a disagree reason for each shift where Agree is unchecked.");
          return;
        }
      }
    }

    setError("");
    setPostSubmitNote("");
    setShowConfirmModal(true);
  };

  const handleConfirmSubmitShiftActuals = async () => {
    const closed = visibleRows.filter(isAccountingWeekClosed);
    const open = visibleRows.filter((r) => !isAccountingWeekClosed(r));
    if (closed.length > 0 && open.length === 0) {
      setError(
        "All shifts on this sheet fall in a closed accounting week and cannot be signed off.",
      );
      setShowConfirmModal(false);
      return;
    }
    if (closed.length > 0 && open.length > 0) {
      setShowConfirmModal(false);
      setShowAttentionModal(true);
      return;
    }
    setShowConfirmModal(false);
    await executeSignoffPost(open);
  };

  const handleAttentionSubmitOpenOnly = async () => {
    const openRows = visibleRows.filter((r) => !isAccountingWeekClosed(r));
    setShowAttentionModal(false);
    await executeSignoffPost(openRows);
  };

  const completeSetupModal = () => {
    const nextSheet =
      clientTimesheetReviewRequired &&
      canManageTimesheetSignoff &&
      signoffIncludeMode === "review_signoffs"
        ? "review"
        : "pending";
    setSignoffSheetKind(nextSheet);
    setShowSetupModal(false);
  };

  const renderSetupFields = () => (
    <>
      {!shiftSettingsLoaded ? (
        <div className="mb-4 text-xs text-gray-500">Loading client shift settings...</div>
      ) : null}
      {clientTimesheetReviewRequired && canManageTimesheetSignoff ? (
        <div className="mb-4">
          <div className="text-xs text-gray-500 mb-2">
            Shifts to include in timesheets sign-off*
          </div>
          <label className="flex items-start gap-2 text-sm mb-2">
            <input
              type="radio"
              name="signoffIncludeMode"
              checked={signoffIncludeMode === "review_signoffs"}
              onChange={() => setSignoffIncludeMode("review_signoffs")}
              style={{ accentColor: "#dc2626" }}
              className="mt-0.5"
            />
            <span>Review sign-offs</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="signoffIncludeMode"
              checked={signoffIncludeMode === "signoff_on_shifts"}
              onChange={() => setSignoffIncludeMode("signoff_on_shifts")}
              style={{ accentColor: "#dc2626" }}
              className="mt-0.5"
            />
            <span>Sign-off on shifts</span>
          </label>
        </div>
      ) : null}

      {clientDefaultBreakMinutes > 0 && (
        <div className="mb-4">
          <div className="text-xs text-gray-500 mb-2">
            Deduct 30-minute break for all shifts or decide on each shift?*
          </div>
          <label className="flex items-start gap-2 text-sm mb-2">
            <input
              type="radio"
              name="breakOption"
              checked={deductBreakAllShifts}
              onChange={() => setDeductBreakAllShifts(true)}
              style={{ accentColor: "#dc2626" }}
              className="mt-0.5"
            />
            <span>Yes, deduct 30 min for all shifts</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="breakOption"
              checked={!deductBreakAllShifts}
              onChange={() => setDeductBreakAllShifts(false)}
              style={{ accentColor: "#dc2626" }}
              className="mt-0.5"
            />
            <span>No, I&apos;ll review each shift</span>
          </label>
        </div>
      )}

      <div className="mb-5">
        <div className="text-xs text-gray-500 mb-2">How would you like to sign off timesheets?*</div>
        <label className="flex items-start gap-2 text-sm mb-2">
          <input
            type="radio"
            name="signoffMode"
            checked={signoffMode === "all"}
            onChange={() => setSignoffMode("all")}
            style={{ accentColor: "#dc2626" }}
            className="mt-0.5"
          />
          <span>Sign-off all days in a single step</span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="signoffMode"
            checked={signoffMode === "day"}
            onChange={() => setSignoffMode("day")}
            style={{ accentColor: "#dc2626" }}
            className="mt-0.5"
          />
          <span>Sign-off one day at a time</span>
        </label>
      </div>
    </>
  );

  const handleLogoClick = () => {
    const urlParams = new URLSearchParams(location.search);
    if (!urlParams.has("clientId") && !urlParams.has("name") && !urlParams.has("client")) {
      navigate("/device");
      return;
    }
    const checkinParams = new URLSearchParams();
    if (clientId) checkinParams.set("clientId", String(clientId));
    if (manageContext.shiftDate) checkinParams.set("shiftDate", manageContext.shiftDate);
    if (manageContext.startDate) checkinParams.set("from", manageContext.startDate);
    if (manageContext.endDate) checkinParams.set("to", manageContext.endDate);
    if (clientName) checkinParams.set("client", clientName);
    if (manageContext.locationName) checkinParams.set("location", manageContext.locationName);
    
    const forceLoginVal = urlParams.get("forceLogin") || "1";
    checkinParams.set("forceLogin", forceLoginVal);

    navigate(`/checkin?${checkinParams.toString()}`);
  };

  return (
    <div
      className={
        portalMode
          ? "flex flex-col min-h-0 flex-1 overflow-hidden bg-white"
          : "h-screen bg-gray-50 flex flex-col overflow-hidden"
      }
    >
      {!portalMode ? (
      <div className="flex justify-between items-start p-4">
        <div className="cursor-pointer" onClick={handleLogoClick}>
          <img src={talentLogo2} alt="Talent Logo" className="h-12 w-auto" />
        </div>

        <div className="text-right">
          <div className="text-xl font-bold text-gray-800">
            {formatAppTime(new Date())}
          </div>
          <div className="text-sm text-gray-600">{clientName}</div>
        </div>
      </div>
      ) : null}

      {portalMode && !showSetupModal ? (
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Sign-off on timesheets</h2>
          <button
            type="button"
            onClick={handleExit}
            className="text-gray-500 hover:text-gray-800 text-2xl leading-none px-2"
            aria-label="Close"
          >
            ×
          </button>
        </div>
      ) : null}

      {!portalMode ? (
      <div className="px-4 pb-2 text-center text-2xl text-gray-700">Sign-off on timesheets</div>
      ) : null}

      {(!portalMode || !showSetupModal) && (
      <>

      {signoffMode === "day" && activeOrderedDateKeys.length > 1 && (
        <div className="px-4 pb-3">
          <select
            value={selectedDateKey}
            onChange={(e) => setSelectedDateKey(e.target.value)}
            className="w-full border border-[#d7d7d7] bg-white rounded px-3 py-2 text-sm text-gray-700 shadow-sm"
          >
            {activeOrderedDateKeys.map((key) => (
              <option key={key} value={key}>
                {dateHeader(key)}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex-1 overflow-auto px-4 pb-4">
        {loading && <div className="text-sm text-gray-500">Loading signoff sheet...</div>}
        {!loading && !visibleRows.length && (
          <div className="mx-auto mt-6 flex min-h-[180px] max-w-md items-center justify-center rounded border border-gray-200 bg-white px-6 py-8 text-center shadow-sm">
            <div>
              <div className="text-base font-medium text-gray-700">
                {signoffSheetKind === "review"
                  ? "No shifts pending manager review"
                  : "No pending signoff shifts"}
              </div>
              <div className="mt-2 text-sm text-gray-500">
                {signoffSheetKind === "review"
                  ? "There are no timesheets waiting for manager review for this selection."
                  : "There are no pending signoff shifts for this selection."}
              </div>
            </div>
          </div>
        )}

        {visibleDateKeys.map((key) => {
          const rows = activeGroups[key] || [];
          if (!rows.length) return null;
          const locationGroups = rows.reduce((acc, row) => {
            const locationLabel = row?.locationName || "Location";
            if (!acc[locationLabel]) acc[locationLabel] = [];
            acc[locationLabel].push(row);
            return acc;
          }, {});

          return (
            <div key={key} className="mb-5 overflow-hidden rounded border border-[#d8d8d8] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <div className="px-4 py-3 text-center text-[18px] font-medium text-[#2e4d70]">
                {dateHeader(key)}
              </div>
              {Object.entries(locationGroups).map(([locationLabel, locationRows]) => {
                const totalHours = locationRows.reduce((sum, row) => {
                  const ra = actualMap[String(row.id)] || {};
                  const br = resolveRowBreakMinutes(
                    row,
                    deductBreakAllShifts,
                    ra,
                    clientDefaultBreakMinutes,
                    clientModifyUnpaidBreak,
                  );
                  return (
                    sum +
                    hoursValueMinutes(
                      fromDateTimeLocalValue(ra.actualStartDate) || row.startIso,
                      fromDateTimeLocalValue(ra.actualEndDate) || row.endIso,
                      br,
                    )
                  );
                }, 0);

                return (
                  <div key={`${key}-${locationLabel}`} className="border-t border-[#d8d8d8]">
              <div className="grid grid-cols-[1fr_auto] items-stretch border-t border-[#d8d8d8] bg-[#efefef]">
                <div className="px-4 py-4 text-[17px] text-[#3d3d3d]">{locationLabel}</div>
                <div className="flex min-w-[108px] flex-col items-center justify-center border-l border-[#d8d8d8] px-3 py-3 text-[#2d4b6a]">
                  <div className="text-[18px] leading-none">{formatHoursTruncated(totalHours)}h</div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.08em]">Hours</div>
                  <div className="text-[11px] uppercase tracking-[0.08em]">{locationRows.length}/{locationRows.length} Shifts</div>
                </div>
              </div>
              <div className="hidden border-t border-[#d8d8d8] px-4 py-3 text-[11px] uppercase tracking-[0.06em] text-[#7d7d7d] md:grid md:grid-cols-[1.25fr_repeat(4,minmax(0,0.7fr))_0.55fr] md:gap-2">
                <div />
                <div>Check In</div>
                <div>Break</div>
                <div>Check Out</div>
                <div>Total After Break</div>
                <div className="text-right">Agree?</div>
              </div>

              {locationRows.map((row) => (
                (() => {
                  const rowActual = actualMap[String(row.id)] || {};
                  const rawCheckInValue = rowActual.actualStartDate || "";
                  const rawCheckOutValue = rowActual.actualEndDate || "";
                  const checkInValue = String(rawCheckInValue).startsWith("1970-01-01")
                    ? ""
                    : rawCheckInValue;
                  const checkOutValue = String(rawCheckOutValue).startsWith("1970-01-01")
                    ? ""
                    : rawCheckOutValue;
                  const hasCheckIn = !!checkInValue;
                  const hasCheckOut = !!checkOutValue;
                  const hasActualInOut = hasCheckIn && hasCheckOut;
                  const hasNeitherActualTime = !hasCheckIn && !hasCheckOut;
                  const hasBothOrNeither = hasCheckIn === hasCheckOut;
                  const agreed = !!agreeMap[String(row.id)] && hasBothOrNeither;
                  const weekClosed = isAccountingWeekClosed(row);
                  const isReviewSheet = signoffSheetKind === "review";
                  const rowBreakMinutes = resolveRowBreakMinutes(
                    row,
                    deductBreakAllShifts,
                    rowActual,
                    clientDefaultBreakMinutes,
                    clientModifyUnpaidBreak,
                  );
                  const editableBreakMinutes = Number(
                    rowActual?.actualUnpaidBreakMinutes ??
                      row?.actualUnpaidBreakMinutes ??
                      defaultReviewBreakMinutesForRow(row, clientDefaultBreakMinutes),
                  );
                  const normalizedEditableBreakMinutes = Number.isFinite(editableBreakMinutes)
                    ? Math.max(0, editableBreakMinutes)
                    : defaultReviewBreakMinutesForRow(row, clientDefaultBreakMinutes);
                  const canEditBreakPerShift = clientModifyUnpaidBreak;
                  const displayBreakMinutes = canEditBreakPerShift
                    ? normalizedEditableBreakMinutes
                    : hasActualInOut
                      ? Math.max(rowBreakMinutes, clientDefaultBreakMinutes)
                      : 0;
                  const totalAfterBreakMinutes = hasActualInOut ? displayBreakMinutes : 0;
                  const editedHours = roundedHoursAfterBreakMinutes(
                    checkInValue,
                    checkOutValue,
                    totalAfterBreakMinutes,
                    clientRoundingSettings,
                    row,
                  );
                  const breakLabel =
                    displayBreakMinutes > 0 ? `${displayBreakMinutes}m` : "0m";

                  return (
                    <div key={row.id} className="border-t border-[#e6e6e6]">
                  <div className="grid gap-3 px-3 py-3 text-sm md:grid-cols-[1.25fr_repeat(4,minmax(0,0.7fr))_0.55fr] md:items-center md:gap-2 md:px-4">
                    <div className="flex min-w-0 items-center gap-3">
                      <StaffAvatarCircle
                        accountId={row.assignedAccountId}
                        employee={manageRowStaffEmployee(row)}
                        name={row.staffName}
                        className="h-9 w-9 shrink-0"
                        textClassName="text-xs font-semibold text-gray-700"
                      />
                      <div className="min-w-0">
                        <div className="mt-1 truncate text-xs text-[#6b7280] md:block">
                          {formatRange(row.startIso, row.endIso)}
                        </div>
                        <div className="truncate font-medium text-[#333]">{row.staffName}</div>
                        <div className="truncate text-xs text-[#666]">{row.position}</div>
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] uppercase tracking-[0.04em] text-[#7d7d7d] md:hidden">
                        Check In
                      </label>
                      <button
                        type="button"
                        disabled={agreed || weekClosed}
                        onClick={() => openAttendancePicker(row, "checkin")}
                        className="flex w-full min-w-0 items-center gap-2 rounded border border-[#d7d7d7] bg-white px-2 py-2 text-left text-[12px] text-[#4b5563] disabled:bg-gray-100 disabled:text-gray-500 md:py-1 md:text-[11px]"
                      >
                        <span className="min-w-0 flex-1 truncate">
                          {formatPickerDisplayValue(checkInValue) || "mm/dd/yyyy --:-- --"}
                        </span>
                        <span aria-hidden="true" className="shrink-0 text-gray-500">
                          📅
                        </span>
                      </button>
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] uppercase tracking-[0.04em] text-[#7d7d7d] md:hidden">
                        Break
                      </label>
                      {canEditBreakPerShift ? (
                        <input
                          type="number"
                          min="0"
                          step="5"
                          value={displayBreakMinutes}
                          disabled={agreed || weekClosed}
                          onChange={(e) =>
                            setActualMap((prev) => ({
                              ...prev,
                              [String(row.id)]: {
                                ...(prev[String(row.id)] || {}),
                                actualUnpaidBreakMinutes: e.target.value === "" ? "" : Number(e.target.value),
                              },
                            }))
                          }
                          className="w-full min-w-0 rounded border border-[#d7d7d7] px-2 py-2 text-[12px] text-[#4b5563] disabled:bg-gray-100 disabled:text-gray-500 md:py-1 md:text-[11px]"
                        />
                      ) : (
                        <div className="text-[#666]">{breakLabel}</div>
                      )}
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] uppercase tracking-[0.04em] text-[#7d7d7d] md:hidden">
                        Check Out
                      </label>
                      <button
                        type="button"
                        disabled={agreed || weekClosed}
                        onClick={() => openAttendancePicker(row, "checkout")}
                        className="flex w-full min-w-0 items-center gap-2 rounded border border-[#d7d7d7] bg-white px-2 py-2 text-left text-[12px] text-[#4b5563] disabled:bg-gray-100 disabled:text-gray-500 md:py-1 md:text-[11px]"
                      >
                        <span className="min-w-0 flex-1 truncate">
                          {formatPickerDisplayValue(checkOutValue) || "mm/dd/yyyy --:-- --"}
                        </span>
                        <span aria-hidden="true" className="shrink-0 text-gray-500">
                          📅
                        </span>
                      </button>
                    </div>
                    <div className="flex items-center justify-between gap-3 font-medium text-[#334e68] md:block">
                      <span className="text-[11px] font-normal uppercase tracking-[0.04em] text-[#7d7d7d] md:hidden">
                        Total After Break
                      </span>
                      <span>
                      {hasNeitherActualTime ? "No Show" : editedHours}
                      </span>
                    </div>
                      <div className="flex items-center justify-between gap-3 md:justify-end">
                        <span className="text-[11px] uppercase tracking-[0.04em] text-[#7d7d7d] md:hidden">
                          Agree?
                        </span>
                        <input
                          type="checkbox"
                        checked={agreed}
                        disabled={weekClosed}
                        onChange={(e) =>
                          setAgreeMap((prev) => ({
                            ...prev,
                            [String(row.id)]: e.target.checked,
                          }))
                        }
                        style={{ accentColor: "#dc2626" }}
                      />
                    </div>
                      </div>
                      {isReviewSheet && row.clientContactName ? (
                        <div className="px-4 pb-1 text-[11px] text-gray-600">
                          Signed off by {row.clientContactName}
                        </div>
                      ) : null}
                      {!agreed && !weekClosed && (
                        <div className="px-3 pb-2 md:px-4">
                          <label className="block text-[11px] text-gray-600 mb-1">
                            Disagree reason *
                          </label>
                          <textarea
                            value={disagreeMap[String(row.id)] || ""}
                            onChange={(e) =>
                              setDisagreeMap((prev) => ({
                                ...prev,
                                [String(row.id)]: e.target.value,
                              }))
                            }
                            rows={2}
                            className="w-full rounded border border-[#d7d7d7] px-2 py-1 text-[11px] text-[#4b5563]"
                          />
                        </div>
                      )}
                      <div className="flex justify-end px-3 pb-3 gap-2 flex-wrap md:px-4">
                        <span
                          className={
                            isReviewSheet
                              ? "inline-flex rounded px-3 py-1 text-[11px] bg-amber-100 text-amber-800"
                              : `inline-flex rounded px-3 py-1 text-[11px] ${statusBadgeClasses(row.stateRaw)}`
                          }
                        >
                          {normalizeState(row.stateRaw).includes("noshow")
                            ? "No show"
                            : isReviewSheet
                              ? "Pending signoff review"
                              : "Pending signoff"}
                        </span>
                        {isAccountingWeekClosed(row) && (
                          <span className="inline-flex rounded px-3 py-1 text-[11px] bg-amber-100 text-amber-900 border border-amber-200">
                            Closed accounting week
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })()
              ))}
                  </div>
                );
              })}
            </div>
          );
        })}

        {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
        {postSubmitNote && (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 mb-2">
            <p>{postSubmitNote}</p>
            {!portalMode ? (
            <button
              type="button"
              onClick={handleExit}
              className="mt-2 text-sm font-medium text-emerald-800 underline"
            >
              Return to dashboard
            </button>
            ) : null}
          </div>
        )}

        <div className="text-center space-y-2 pt-2">
          <button
            type="button"
            onClick={handleSubmitActuals}
            disabled={
              submitting ||
              loading ||
              !visibleRows.length ||
              (signoffSheetKind === "review"
                ? !canManageTimesheetSignoff
                : capabilities?.canSignoffTimesheets !== true)
            }
            className="text-red-500 text-sm font-semibold disabled:text-gray-400"
          >
            {submitting
              ? "SUBMITTING..."
              : signoffSheetKind === "review"
                ? "COMPLETE MANAGER REVIEW"
                : "SIGN OFF"}
          </button>
          <button
            type="button"
            onClick={handleExit}
            className="block w-full text-center text-gray-700 text-sm"
          >
            CANCEL
          </button>
        </div>
      </div>
      </>
      )}

      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center z-[60] px-4">
          <div className="bg-white w-full max-w-md rounded shadow-xl p-5">
            <h2 className="text-xl text-center text-gray-800 mb-3">Confirm</h2>
            <p className="text-sm text-gray-600 mb-4 text-center">
              {signoffSheetKind === "review"
                ? "You are about to record final manager signoff for the shifts below. You may adjust check-in, check-out, or break first; any edits are saved with this signoff."
                : "You are about to submit shift actuals for the shifts on this sheet. Please confirm that the check-in, check-out, breaks, and agree or disagree selections are correct before continuing."}
            </p>
            <div className="flex flex-col gap-2 items-center">
              <button
                type="button"
                onClick={() => handleConfirmSubmitShiftActuals()}
                disabled={submitting}
                className="text-red-500 text-sm font-semibold disabled:text-gray-400"
              >
                {submitting
                  ? "Submitting…"
                  : signoffSheetKind === "review"
                    ? "Confirm manager signoff"
                    : "Submit shift actuals"}
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => setShowConfirmModal(false)}
                className="text-gray-700 text-sm disabled:text-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showAttentionModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center z-[60] px-4">
          <div className="bg-white w-full max-w-md rounded shadow-xl p-5">
            <h2 className="text-xl text-center text-gray-800 mb-3">Attention</h2>
            <p className="text-sm text-gray-600 mb-2">
              Some shifts fall in a <span className="font-medium">closed accounting week</span>. Only shifts in an{" "}
              <span className="font-medium">open</span> week can be signed off from this sheet.
            </p>
            <p className="text-sm text-gray-700 mb-4">
              {closedWeekRowCounts.open} shift{closedWeekRowCounts.open === 1 ? "" : "s"} will be submitted.{" "}
              {closedWeekRowCounts.closed} shift{closedWeekRowCounts.closed === 1 ? "" : "s"} will be skipped.
            </p>
            <div className="flex flex-col gap-2 items-center">
              <button
                type="button"
                onClick={() => handleAttentionSubmitOpenOnly()}
                disabled={submitting}
                className="text-red-500 text-sm font-semibold disabled:text-gray-400"
              >
                {submitting ? "Submitting…" : "Submit open-week shifts only"}
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => {
                  setShowAttentionModal(false);
                  setShowConfirmModal(true);
                }}
                className="text-gray-700 text-sm disabled:text-gray-400"
              >
                Go back
              </button>
            </div>
          </div>
        </div>
      )}

      {attendancePicker && (
        <DateTimePickerPanel
          value={attendancePicker.value}
          minDateTime={attendancePicker.minDateTime}
          onChange={(date) => applyAttendancePickerValue(date)}
          onDone={(date) => {
            applyAttendancePickerValue(date);
            setAttendancePicker(null);
          }}
          onBack={() => setAttendancePicker(null)}
        />
      )}

      {showSetupModal && (
        portalMode ? (
          <div className="p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Time sheet sign-off</h2>
              <button
                type="button"
                onClick={handleExit}
                className="text-gray-500 hover:text-gray-800 text-2xl leading-none px-2"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            {renderSetupFields()}
            <div className="flex justify-end gap-4 mt-4">
              <button
                type="button"
                onClick={handleExit}
                className="text-gray-600 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={completeSetupModal}
                disabled={!shiftSettingsLoaded}
                className="text-red-500 text-sm font-semibold disabled:text-gray-400"
              >
                OK
              </button>
            </div>
          </div>
        ) : (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center z-50 px-4">
          <div className="bg-white w-full max-w-md rounded shadow-xl p-5">
            <div className="mb-4 flex items-start justify-between gap-3">
              <h2 className="flex-1 text-xl text-center text-gray-800">Time sheet sign-off</h2>
              <button
                type="button"
                onClick={handleExit}
                className="text-gray-500 hover:text-gray-800 text-2xl leading-none px-1 -mt-1"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            {renderSetupFields()}
            <button
              type="button"
              onClick={completeSetupModal}
              disabled={!shiftSettingsLoaded}
              className="text-red-500 text-sm font-semibold disabled:text-gray-400"
            >
              OK
            </button>
          </div>
        </div>
        )
      )}
    </div>
  );
}
//this is a comment